/* Shared analytics wrapper — fans out to GA4 (gtag), PostHog, and Clarity.
 *
 * Both surfaces no-op when their respective globals/keys are missing, so
 * unconditional calls are safe from SSR and from pages that never engage with
 * analytics. PostHog is loaded lazily via `PostHogLoader` — see
 * `@/lib/analytics/posthog` for init/capture semantics.
 *
 * For NEW export-side events, prefer `trackExport()` from
 * `@/lib/analytics/posthog` — it's typed against the event registry. This
 * wrapper stays loose-typed to preserve the existing GA4 call sites
 * (debate_view / niche_generate / onboarding_step / etc.).
 */

import { capturePostHog } from "@/lib/analytics/posthog";
import { mirrorAnalyticsEvent } from "@/lib/analytics/fanout";
import { captureMetaFromAnalyticsEvent } from "@/lib/analytics/meta-pixel";

type EventParams = Record<string, string | number | boolean | undefined>;

/**
 * Canonical event names. Keep these in one place so analytics taxonomy stays
 * consistent across components (rename events here, not inline at call sites).
 *
 * Export funnel events live in `@/lib/analytics/posthog::EXPORT_EVENTS` —
 * they're typed and consumed via `trackExport()` directly.
 */
export const ANALYTICS_EVENTS = {
  /** Fired when user picks one of the generated niches (card click). */
  NICHE_SELECT: "niche_select",
} as const;

export function trackEvent(name: string, params?: EventParams) {
  if (typeof window === "undefined") return;
  // GA4 receives a snake_case-safe alias for dotted product events
  // (`billing.checkout.start` -> `billing_checkout_start`).
  mirrorAnalyticsEvent(name, params);
  // Fan-out to PostHog — silent no-op if NEXT_PUBLIC_POSTHOG_KEY is unset.
  capturePostHog(name, params, { mirror: false });
  // Fan-out selected launch-funnel events to Meta Pixel.
  captureMetaFromAnalyticsEvent(name, params);
}

/**
 * Fire an event at most once per (name, dedupKey) pair, persisted in localStorage.
 * Use for completion/view events that should count once per artifact, not per page load.
 * Returns true if the event was sent, false if suppressed as a duplicate.
 */
export function trackEventOnce(name: string, dedupKey: string, params?: EventParams): boolean {
  if (typeof window === "undefined") return false;
  const storageKey = `analytics_once:${name}:${dedupKey}`;
  try {
    if (localStorage.getItem(storageKey)) return false;
    localStorage.setItem(storageKey, "1");
  // intentional: localStorage unavailable (private mode / quota) — fall through and still track to avoid losing signal
  } catch {
    // localStorage unavailable (private mode / quota) — fall through and still track to avoid losing signal.
  }
  trackEvent(name, params);
  return true;
}

/**
 * Fire an event at most once per (name, dedupKey) pair, per browser tab/session,
 * persisted in sessionStorage. Sister of `trackEventOnce` — same shape, but the
 * dedupe scope is the current tab session (cleared on tab close / new tab) rather
 * than permanent.
 *
 * Use for funnel signals that should re-fire on each new visit but NOT re-fire
 * on back/forward navigation within the same tab — e.g. landing scene_view,
 * landing scroll_depth thresholds.
 *
 * Returns true if the event was sent, false if suppressed as a duplicate.
 */
export function trackEventSessionOnce(
  name: string,
  dedupKey: string,
  params?: EventParams,
): boolean {
  if (typeof window === "undefined") return false;
  const storageKey = `analytics_session_once:${name}:${dedupKey}`;
  try {
    if (sessionStorage.getItem(storageKey)) return false;
    sessionStorage.setItem(storageKey, "1");
  // intentional: sessionStorage unavailable (private mode / quota) — fall through and still track to avoid losing signal
  } catch {
    // sessionStorage unavailable (private mode / quota) — fall through and still track to avoid losing signal.
  }
  trackEvent(name, params);
  return true;
}
